import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  useInfiniteQuery,
  useQueryClient,
  type InfiniteData,
  type QueryKey,
} from '@tanstack/react-query'
import { rankItem } from '@tanstack/match-sorter-utils'
import { type Conversation } from '../lib/db'
import { sendCommand } from '../lib/extension'
import { useAuth } from '../lib/auth'
import { useDebouncedValue } from './useDebounced'

const DEBOUNCE_MS = 600

/**
 * Which backend, if any, failed on this page. Null when both succeeded
 * (or only one was attempted and it succeeded). Exposed so the UI can
 * surface a "LinkedIn results only — Sales Nav unavailable" banner
 * without treating the page as a total failure.
 */
export type PartialFailure = 'regular' | 'sales' | null

interface SearchPage {
  conversations: Conversation[]
  nextCursor: string | null
  partialFailure: PartialFailure
}

type RegularResult = PromiseSettledResult<{
  conversations: Conversation[]
  nextCursor: string | null
}>
type SalesResult = PromiseSettledResult<{ conversations: Conversation[] }>

/**
 * Merge the two allSettled outcomes into a SearchPage, or throw when
 * the result is a total failure.
 *
 * Rules:
 *   - If the regular backend rejected AND sales either also rejected
 *     or wasn't attempted, THROW. The calling useInfiniteQuery will
 *     set `query.error`, which the hook surfaces as `searchError`.
 *   - If regular rejected but sales succeeded, return sales results
 *     with `partialFailure: 'regular'` + null cursor (regular owns
 *     pagination; we can't advance past this page).
 *   - If regular succeeded but sales rejected, return regular results
 *     with `partialFailure: 'sales'` + regular's cursor.
 *   - If both succeeded, return combined results with
 *     `partialFailure: null`.
 *
 * Exported for unit tests. Pure; no hooks or closures.
 */
export function mergeSearchPage(params: {
  regularResult: RegularResult
  salesResult: SalesResult
  salesAttempted: boolean
}): SearchPage {
  const { regularResult, salesResult, salesAttempted } = params
  const regularFailed = regularResult.status === 'rejected'
  const salesFailed = salesAttempted && salesResult.status === 'rejected'

  // Total failure: the only attempted backend(s) all rejected. Throw so
  // the query lands in an error state rather than "silently zero
  // results" — the exact shape of the review-flagged bug (finding 6).
  if (regularFailed && (salesFailed || !salesAttempted)) {
    const reason = regularResult.status === 'rejected'
      ? regularResult.reason
      : (salesResult as PromiseRejectedResult).reason
    throw reason instanceof Error ? reason : new Error(String(reason))
  }

  const regular = regularResult.status === 'fulfilled'
    ? (regularResult.value?.conversations ?? [])
    : []
  const sales = salesResult.status === 'fulfilled'
    ? (salesResult.value?.conversations ?? [])
    : []
  const nextCursor = regularResult.status === 'fulfilled'
    ? (regularResult.value?.nextCursor ?? null)
    : null

  const partialFailure: PartialFailure =
    regularFailed ? 'regular'
    : salesFailed ? 'sales'
    : null

  return { conversations: [...regular, ...sales], nextCursor, partialFailure }
}

// User-scoped key so cached results from one account never surface under
// another. Data comes from the extension (LinkedIn session), not Supabase
// — no RLS backstop if two users share a browser.
const searchKey = (userId: string | undefined, keywords: string, hasSalesNav: boolean): QueryKey =>
  ['search', 'conversations', userId ?? 'anon', keywords, hasSalesNav]

/**
 * Clears every cached search result. Called on sign-out so a new user
 * doesn't see the previous user's cached queries.
 *
 * We can't wipe from module scope here (no access to the QueryClient), so
 * each mounted useSearch instance registers its clear callback into a Set.
 * A Set rather than a singleton because:
 *   - StrictMode dev double-invokes effects (mount → unmount → mount). With
 *     a singleton `let _clearFn`, the second mount's cleanup (which runs
 *     on the first unmount) would null out the newly-registered fn.
 *   - Two useSearch instances can legitimately co-exist during a page
 *     transition (old view fading out, new view fading in). A singleton
 *     would lose whichever registered second on the first unmount.
 * The Set uses identity equality so add/delete are symmetric even across
 * StrictMode's synthetic remount.
 */
const _clearFns = new Set<() => void>()
export function clearSearchCache() {
  for (const fn of _clearFns) fn()
}

export function useSearch({ hasSalesNav = false }: { hasSalesNav?: boolean } = {}) {
  const qc = useQueryClient()
  const { user } = useAuth()
  const userId = user?.id
  // React-router hooks so the `?query=...` sync goes through the
  // router rather than `window.history.replaceState` (which bypassed
  // `useLocation()` observers, leaving any caller keyed on
  // `location.search` stale — see review HIGH #7).
  const navigate = useNavigate()
  const location = useLocation()

  // Register the module-level clear hook so AuthProvider.signOut still works.
  useEffect(() => {
    const fn = () => qc.removeQueries({ queryKey: ['search'] })
    _clearFns.add(fn)
    return () => { _clearFns.delete(fn) }
  }, [qc])

  // ─── Query + debounce ────────────────────────────────────────────────────
  // Restore initial query from URL for deep-linkable search.
  const initialQuery = typeof window !== 'undefined'
    ? (new URLSearchParams(window.location.search).get('query') || '')
    : ''
  const [searchMode, setSearchMode] = useState(!!initialQuery)
  const [searchQuery, setSearchQuery] = useState(initialQuery)
  const [searchError, setSearchError] = useState<string | null>(null)

  // Debounce keystrokes into the query key so TQ only fires per stable input.
  // Empty strings pass through immediately (no request to debounce away).
  const trimmed = searchQuery.trim()
  const debouncedRaw = useDebouncedValue(searchQuery, DEBOUNCE_MS)
  const debouncedQuery = trimmed.length === 0 ? '' : debouncedRaw

  // Keep URL in sync with query. Uses react-router's `navigate(..., {replace})`
  // so `useLocation()` observers upstream can pick up the `?query=...`
  // addition/removal. No-op when the new search string matches the
  // current one — otherwise the effect would rewrite the URL on every
  // unrelated render, fighting with `navigate(...)` from filter clicks.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const existing = params.get('query') ?? ''
    const trimmedQuery = searchQuery.trim()
    if (trimmedQuery === existing) return
    if (trimmedQuery) params.set('query', searchQuery)
    else params.delete('query')
    const nextSearch = params.toString()
    const nextTarget = `${location.pathname}${nextSearch ? `?${nextSearch}` : ''}${location.hash}`
    navigate(nextTarget, { replace: true })
  }, [searchQuery, navigate, location.pathname, location.search, location.hash])

  // ─── Infinite query ──────────────────────────────────────────────────────
  // One query that fires both regular + Sales Nav in parallel and merges the
  // results. Pagination cursor comes from the regular side (Sales Nav doesn't
  // paginate in the original implementation).
  const query = useInfiniteQuery<SearchPage, Error, InfiniteData<SearchPage>, QueryKey, string | null>({
    queryKey: searchKey(userId, debouncedQuery, hasSalesNav),
    enabled: !!userId && debouncedQuery.trim().length > 0,
    initialPageParam: null,
    staleTime: 10 * 60_000, // results are effectively immutable for 10min
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    queryFn: async ({ pageParam, signal }) => {
      const cursor = pageParam as string | null
      // Sales Nav is only attempted on the first page and only when
      // the user has the feature — subsequent pages paginate regular
      // only. A synthesized fulfilled-empty keeps the tuple shape
      // stable for allSettled.
      const salesAttempted = hasSalesNav && cursor == null
      const [regularResult, salesResult] = await Promise.allSettled([
        sendCommand<{ conversations: Conversation[]; nextCursor: string | null }>(
          'SEARCH_CONVERSATIONS',
          { keywords: debouncedQuery, count: 25, ...(cursor ? { cursor } : {}) },
          { signal },
        ),
        salesAttempted
          ? sendCommand<{ conversations: Conversation[] }>(
              'SALES_SEARCH_CONVERSATIONS',
              { keywords: debouncedQuery, count: 25 },
              { signal },
            )
          : Promise.resolve({ conversations: [] as Conversation[] }),
      ])
      return mergeSearchPage({ regularResult, salesResult, salesAttempted })
    },
  })

  // Surface query errors through the existing searchError slot so the UI
  // doesn't need to switch error sources.
  useEffect(() => {
    if (query.error) setSearchError('Search failed — try again')
    else setSearchError(null)
  }, [query.error])

  // ─── Derived ─────────────────────────────────────────────────────────────
  const flat = useMemo(
    () => (query.data?.pages ?? []).flatMap((p) => p.conversations),
    [query.data],
  )
  const results = useMemo(
    () => (query.isSuccess ? rerank(flat, debouncedQuery) : []),
    [query.isSuccess, flat, debouncedQuery],
  )

  // Only reflect "loading" when we've actually enabled the query. Otherwise
  // `isPending` is perma-true for disabled queries and the search UI shows a
  // spinner forever when there's nothing to search for (or no signed-in user).
  const apiLoading = !!userId && debouncedQuery.trim().length > 0 && query.isFetching
  const nextCursor = query.data?.pages.at(-1)?.nextCursor ?? null
  // Expose the most recent page's partial-failure state. Pages that
  // succeed fully reset it; pages with a single-backend outage
  // surface it until the next clean page. Null while no query is
  // active.
  const partialFailure: PartialFailure = query.data?.pages.at(-1)?.partialFailure ?? null

  // ─── Controls ────────────────────────────────────────────────────────────
  const enterSearch = useCallback(() => setSearchMode(true), [])
  const exitSearch = useCallback(() => {
    setSearchMode(false)
    setSearchQuery('')
    // debouncedQuery is derived via useDebouncedValue; clearing searchQuery
    // causes trimmed.length===0 which short-circuits debouncedQuery to ''
    // on the next render — no explicit reset needed.
  }, [])
  const loadMore = useCallback(() => {
    if (!query.hasNextPage || query.isFetchingNextPage) return
    query.fetchNextPage()
  }, [query])

  return {
    searchMode,
    searchQuery,
    setSearchQuery,
    results,
    apiLoading,
    searchError,
    partialFailure,
    nextCursor,
    enterSearch,
    exitSearch,
    loadMore,
  }
}

// Re-sort API results by local fuzzy relevance (best name match first)
function rerank(convs: Conversation[], query: string): Conversation[] {
  if (!query.trim()) return convs
  return [...convs]
    .map((conv) => ({
      conv,
      rank: rankItem(conv, query, {
        accessors: [
          (c: Conversation) => c.participantName || '',
          (c: Conversation) => (c.allParticipants || []).map((p) => p.name).join(', '),
          (c: Conversation) => c.lastMessageText || '',
          (c: Conversation) => c.participantHeadline || '',
        ],
      }).rank ?? 0,
    }))
    .sort((a, b) => b.rank - a.rank)
    .map(({ conv }) => conv)
}
